import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Label,
  Input as RInput,
  InputGroup,
  InputGroupText,
  Tooltip,
  TabContent,
  TabPane,
} from "reactstrap";
import cx from "classnames";

import { getRandomString } from "../../_helpers/utils";
import { Icon } from "./icon";

import global from "../../scss/dhp.scss";
import userAvatar from "../../assets/images/ui-user.svg";
import TabSchemeSelector from "../Common/TabSchemeSelector";
import CreateCollectionModal from "../Collections/Modal/CreateCollectionModal";

let style = Object.assign({}, global);

const _helpers = {
  concat: (fields, data) => {
    let textToDisplay = "";
    fields.forEach(field => (textToDisplay += data[field] ? `${data[field]} ` : ""));
    return textToDisplay.trim();
  },
  concat_all: (primary, muted, data) => {
    return _helpers.concat(primary, data) + (muted ? "  -  " + _helpers.concat(muted, data) : "");
  },
};

const Selection = ({ options, value, ...props }) => {
  const [firstItem, setFirstItem] = useState(false);
  const [moreItems, setMoreItems] = useState(0);
  const [placeholder, setPlaceholder] = useState("Choose Options");

  useEffect(() => {
    if (options.length > 0 && value) {
      let firstOption = options.find(option => option[props.optionValue] === value[0]) ?? false;
      setFirstItem(firstOption ? _helpers.concat(props.optionTextPrimary, firstOption) : false);
      setMoreItems(value.length - 1);
    }
  }, [options, value]);

  useEffect(() => {
    if (props?.placeholder) setPlaceholder(props.placeholder);
  }, [props?.placeholder]);

  return (
    <div className={cx(style["align-items-center"], style["d-flex"], style["justify-content-between"], style["w-100"])}>
      <h6 className={cx(style["mb-0"], style["text-truncate"], { [style["text-muted"]]: !firstItem })}>
        {firstItem ? firstItem : placeholder}
      </h6>
      <div className={cx(style["align-items-center"], style["d-flex"], style["ps-3"])}>
        {moreItems > 0 && <span className={style["more-user"]}>+{moreItems}</span>}
        <Icon icon="ui-arrow-down" />
      </div>
    </div>
  );
};

const Option = ({ option, index, id, ...props }) => {
  const textPrimaryRef = useRef();
  const textMutedRef = useRef();
  const thumb = {
    src: option[props?.optionImage] ?? props?.optionImageNoPreview,
    loading: true,
    default: false,
  };
  const colorHexMap = {
    "peach-cream": "#FBE7C6",
    "pastel-teal": "#B7E7E6",
    "blush-pink": "#F5CAC3",
    "powder-blue": "#B6D1E8",
    "mint-green": "#C8DFBB",
    "sand-beige": "#ECD5BB",
    "lavender": "#D4CEF6",
    "arctic-blue": "#A8DADC",
    "sunflower": "#F4E285",
    "apricot": "#FED9B7",
  };
  const [textPrimary, setTextPrimary] = useState("");
  const [textMuted, setTextMuted] = useState("");
  const [optionThumb, setOptionThumb] = useState(thumb);
  const [showTextPrimaryTooltip, setShowTextPrimaryTooltip] = useState(false);
  const [showTextMutedTooltip, setShowTextMutedTooltip] = useState(false);

  const handleImageLoad = () => setOptionThumb({ ...optionThumb, loading: false });

  const handleImageError = () => setOptionThumb({ ...optionThumb, src: userAvatar, loading: false, default: true });

  const toggleTextPrimaryTooltip = () =>
    setShowTextPrimaryTooltip(
      !showTextPrimaryTooltip && textPrimaryRef.current.scrollWidth > textPrimaryRef.current.offsetWidth
    );

  const toggleTextMutedTooltip = () =>
    setShowTextMutedTooltip(
      !showTextMutedTooltip && textMutedRef.current.scrollWidth > textMutedRef.current.offsetWidth
    );

  const changeHandler = e => {
    let targetValue = e.target.getAttribute("data-record-id");
    props.setValue(prev => (e.target.checked ? [...prev, targetValue] : prev.filter(v => v !== targetValue)));
  };

  useEffect(() => {
    setTextPrimary(_helpers.concat(props.optionTextPrimary, option));
    if (props?.optionTextMuted) setTextMuted(_helpers.concat(props.optionTextMuted, option));

    const image = new Image();
    image.src = thumb.src;
    image.addEventListener("load", handleImageLoad);
    image.addEventListener("error", handleImageError);
    return () => {
      image.removeEventListener("load", handleImageLoad);
      image.removeEventListener("error", handleImageError);
    };
  }, []);

  return (
    <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"])}>
      <RInput
        type="checkbox"
        id={`item-selection-${props.uniqueId}-${option[props.optionValue]}`}
        className={style["form-check-input"]}
        data-record-id={option[props.optionValue]}
        onChange={e => changeHandler(e)}
        checked={props.value.includes(option[props.optionValue])}
      />
      <Label
        htmlFor={`item-selection-${props.uniqueId}-${option[props.optionValue]}`}
        className={cx(style["align-items-center"], style["d-flex"], style["form-check-label"])}>
        <div
          className={cx(style["thumbnail-img"], style["rounded"])}
          style={{ background: colorHexMap[option?.color || "peach-cream"] }}>
          {option?.name?.trim()?.charAt(0)?.toUpperCase()}
        </div>
        {props?.optionImage && (
          <div className={cx(style["thumbnail-img"])}>
            {!optionThumb.loading && (
              <img
                src={optionThumb.src}
                className={cx(style["img-fluid"], style["rounded-circle"], {
                  [style["default"]]: optionThumb.default,
                })}
                alt="Option image"
              />
            )}
          </div>
        )}

        <div className={style["user-info"]}>
          <h6
            className={cx(style["m-0"], style["text-truncate"])}
            ref={textPrimaryRef}
            id={`text-primary-tooltip-${id}`}>
            {textPrimary}
          </h6>
          <div
            className={cx(style["text-muted"], style["text-truncate"])}
            ref={textMutedRef}
            id={`text-muted-tooltip-${id}`}>
            {textMuted}
          </div>
        </div>
      </Label>
      <Tooltip
        placement="bottom"
        isOpen={showTextPrimaryTooltip}
        target={`text-primary-tooltip-${id}`}
        toggle={toggleTextPrimaryTooltip}
        boundariesElement={document.getElementById("app")}>
        {textPrimary}
      </Tooltip>
      <Tooltip
        placement="bottom"
        isOpen={showTextMutedTooltip}
        target={`text-muted-tooltip-${id}`}
        toggle={toggleTextMutedTooltip}
        boundariesElement={document.getElementById("app")}>
        {textMuted}
      </Tooltip>
    </div>
  );
};

const TabularMultiSelect = ({ updateState, schemeTypeArray = ["General", "Other"], ...props }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [options, setOptions] = useState({});
  const [value, setValue] = useState([]);
  const [uniqueId, setUniqueId] = useState();
  const [searchKeyLength, setSearchKeyLength] = useState(0);
  const [visibleOptions, setVisibleOptions] = useState(0);
  const [noOptionMessage, setNoOptionMessage] = useState("No options");
  const [noResultMessage, setNoResultMessage] = useState("No results found");
  const [noOutcome, setNoOutcome] = useState({ type: false, message: false });

  const [schemeType, setSchemeType] = useState(schemeTypeArray[0]);
  const activeTabIndex = schemeTypeArray.indexOf(schemeType);
  const history = useHistory();

  const toggle = () => setDropdownOpen(prevState => !prevState);

  const allOptions = schemeTypeArray.flatMap(type => options[type] || []);

  const searchHandler = e => {
    const keyword = e.target.value.trim().toLowerCase();
    const updatedOptions = {};

    schemeTypeArray.forEach(type => {
      updatedOptions[type] = props.options
        .filter(option => option.schemeType === type)
        .map(option => {
          const match = [...props.optionTextPrimary, ...(props.optionTextMuted ?? [])].some(key =>
            option[key]?.toLowerCase().includes(keyword)
          );
          return { ...option, display: match ? "block" : "none" };
        });
    });

    setOptions(updatedOptions);
    setSearchKeyLength(keyword.length);
  };

  const cancelSearch = () => {
    const input = document.getElementById(`search-box-${uniqueId}`);
    if (input && input.value !== "") {
      input.value = "";
      input.focus();

      const resetAllOptions = schemeTypeArray.reduce((acc, type) => {
        const tabOptions = props.options
          .filter(option => option.schemeType === type)
          .map(option => ({ ...option, display: "block" }));
        acc[type] = tabOptions;
        return acc;
      }, {});

      setOptions(resetAllOptions);
      setSearchKeyLength(0);
    }
  };

  const handleAddNewClick = () => {
    sessionStorage.setItem("fromAddCollectionModal", "true");
    props._helpers.modal.setComponent({
      name: CreateCollectionModal,
    });
  };

  const handleSettingsClick = () => {
    if (props?.isSuperAdmin) {
      history.push(props?.manageItemLink);
    }
  };

  useEffect(() => {
    if (Object.keys(options).length > 0) {
      updateState(value);
    }
  }, [value]);

  useEffect(() => {
    if (Object.keys(options).length > 0 && noOptionMessage && noResultMessage) {
      const tabOptions = options[schemeType] || [];
      if (tabOptions.length === 0) setNoOutcome({ type: "no_option_provided", message: noOptionMessage });
      else {
        let visible = tabOptions.filter(option => option.display === "block");
        setNoOutcome(
          visible.length > 0 ? { type: false, message: false } : { type: "no_search_result", message: noResultMessage }
        );
        setVisibleOptions(visible.length);
      }
    }
  }, [options, schemeType, noOptionMessage, noResultMessage]);

  useEffect(() => {
    if (props?.noOptionMessage) setNoOptionMessage(props.noOptionMessage);
    if (props?.noResultMessage) setNoResultMessage(props.noResultMessage);
  }, [props?.noOptionMessage, props?.noResultMessage]);

  useEffect(() => {
    const grouped = {};
    schemeTypeArray.forEach(type => (grouped[type] = []));
    props.options.forEach(option => {
      const type = option.schemeType || schemeTypeArray[0];
      grouped[type] = [...(grouped[type] || []), { ...option, display: "block" }];
    });
    setOptions(grouped);
    if (props?.defaultValue) setValue(props.defaultValue);
  }, [props.options, props?.defaultValue]);

  useEffect(() => {
    if (dropdownOpen) {
      const grouped = {};
      schemeTypeArray.forEach(type => (grouped[type] = []));
      props.options.forEach(option => {
        const type = option.schemeType || schemeTypeArray[0];
        grouped[type] = [...(grouped[type] || []), { ...option, display: "block" }];
      });
      setOptions(grouped);
    }
  }, [dropdownOpen]);

  useEffect(() => {
    setUniqueId(getRandomString(18));
  }, []);

  return (
    <UncontrolledDropdown
      isOpen={dropdownOpen}
      toggle={toggle}
      className={cx(style["multi-select-dropdown"], {
        [style["with-settings-icon"]]: props?.manageItemLink,
      })}>
      <DropdownToggle caret tag="a" className={style["rounded"]}>
        <Selection options={allOptions} value={value} {...props} />
      </DropdownToggle>
      <DropdownMenu className={cx(style["shadow"], style["border-0"], style["rounded"])}>
        <div className={cx(style["search"], style["mb-2"], style["px-3"], style["py-2"])}>
          <InputGroup className={style["flex-nowrap"]}>
            <div className={style["input-group-prepend"]}>
              <InputGroupText className={style["bg-white"]}>
                <Icon icon="ui-search" />
              </InputGroupText>
            </div>
            <RInput
              type="text"
              autoComplete="off"
              id={`search-box-${uniqueId}`}
              onChange={e => searchHandler(e)}
              autoFocus={true}
            />
            <div
              className={cx(style["input-group-append"], { [style["d-none"]]: searchKeyLength === 0 || props?.isCloseHide })}
              style={{ cursor: "pointer" }}
              onClick={() => cancelSearch()}>
              <InputGroupText className={style["bg-white"]}>
                <Icon icon="ui-close" />
              </InputGroupText>
            </div>
            {props?.manageItemLink && (
              <div className={cx(style["settings-icon"], style["custom-tooltip"])} onClick={handleSettingsClick}>
                <Icon icon={props?.isSuperAdmin ? "ui-settings" : "ui-info"} />
                <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                  {props?.manageItemText}
                </div>
              </div>
            )}
          </InputGroup>
        </div>

        <div className={cx(style["ps-3"], style["pe-3"])}>
          <TabSchemeSelector
            schemeTypeArray={schemeTypeArray}
            setSchemeType={setSchemeType}
            schemeType={schemeType}
            activeTabIndex={activeTabIndex}
          />
        </div>
        <div
          style={{ maxHeight: "150px" }}
          className={cx(style["multi-user-list"], style["customScroll"], style["scroll-Y"])}>
          <TabContent className={cx(style["p-0"], style["overflow-visible"])}>
            {schemeTypeArray.map(type => (
              <TabPane
                key={type}
                tabId={type}
                className={cx({ "d-block": schemeType === type, "d-none": schemeType !== type })}>
                {(options[type] || []).map((option, i) => (
                  <DropdownItem
                    tag="div"
                    key={i}
                    toggle={false}
                    className={cx(
                      { [style["d-none"]]: option?.display === "none" },
                      { [style["disable"]]: option?.sharedPermission === "RO" }
                    )}>
                    <Option
                      id={option.id}
                      option={option}
                      index={i}
                      value={value}
                      setValue={setValue}
                      uniqueId={uniqueId}
                      {...props}
                    />
                  </DropdownItem>
                ))}
                {schemeType === type && noOutcome.type && (
                  <div
                    className={cx(
                      style["d-flex"],
                      style["justify-content-center"],
                      style["py-4"],
                      style["text-muted"]
                    )}>
                    {noOutcome.message}
                  </div>
                )}
              </TabPane>
            ))}
          </TabContent>
        </div>
        {schemeType === "My Collections" && (
          <>
            <DropdownItem divider />
            <DropdownItem tag="div" className={style["d-flex"]} onClick={handleAddNewClick}>
              <Icon icon="ui-plus" additionalclass="me-2" /> New
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

Selection.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  optionValue: PropTypes.string.isRequired,
  optionTextPrimary: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
};

Option.propTypes = {
  option: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  value: PropTypes.array.isRequired,
  setValue: PropTypes.func.isRequired,
  uniqueId: PropTypes.string.isRequired,
  optionValue: PropTypes.string.isRequired,
  optionTextPrimary: PropTypes.array.isRequired,
  optionImage: PropTypes.string,
  optionImageNoPreview: PropTypes.string,
  optionTextMuted: PropTypes.array,
};

TabularMultiSelect.propTypes = {
  updateState: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  optionValue: PropTypes.string.isRequired,
  optionTextPrimary: PropTypes.array.isRequired,
  optionImage: PropTypes.string,
  optionImageNoPreview: PropTypes.string,
  optionTextMuted: PropTypes.array,
  defaultValue: PropTypes.array,
  placeholder: PropTypes.string,
  noOptionMessage: PropTypes.string,
  noResultMessage: PropTypes.string,
};

export default TabularMultiSelect;
